import type { PieceType, Team } from '@halvard/rules';
import { COLORS, TEAM_COLORS } from './palette.js';

interface ModelProps {
  body: string;
  accent: string;
}

function Mat({ color, emissive }: { color: string; emissive?: string }) {
  return (
    <meshStandardMaterial
      color={color}
      roughness={0.75}
      metalness={0}
      emissive={emissive ?? '#000000'}
      emissiveIntensity={emissive ? 0.35 : 0}
    />
  );
}

/** Pedestal, column and an octahedron flame. */
function Beacon({ body, accent }: ModelProps) {
  return (
    <group>
      <mesh position={[0, 0.07, 0]} castShadow>
        <cylinderGeometry args={[0.27, 0.31, 0.14, 24]} />
        <Mat color={body} />
      </mesh>
      <mesh position={[0, 0.34, 0]} castShadow>
        <cylinderGeometry args={[0.09, 0.12, 0.42, 16]} />
        <Mat color={body} />
      </mesh>
      <mesh position={[0, 0.7, 0]} castShadow>
        <octahedronGeometry args={[0.18]} />
        <Mat color={accent} emissive={accent} />
      </mesh>
    </group>
  );
}

/** Hexagonal body carrying an ice axe. */
function Climber({ body, accent }: ModelProps) {
  return (
    <group>
      <mesh position={[0, 0.26, 0]} castShadow>
        <cylinderGeometry args={[0.17, 0.24, 0.52, 6]} />
        <Mat color={body} />
      </mesh>
      <mesh position={[0, 0.6, 0]} castShadow>
        <sphereGeometry args={[0.12, 16, 12]} />
        <Mat color={body} />
      </mesh>
      <group position={[0.24, 0.42, 0]} rotation={[0, 0, -0.35]}>
        <mesh castShadow>
          <boxGeometry args={[0.04, 0.56, 0.04]} />
          <Mat color={accent} />
        </mesh>
        <mesh position={[-0.05, 0.27, 0]} castShadow>
          <boxGeometry args={[0.2, 0.05, 0.05]} />
          <Mat color={accent} />
        </mesh>
      </group>
    </group>
  );
}

/** Four-legged body with two curled horns. Faces the enemy side. */
function Ibex({ body, accent }: ModelProps) {
  return (
    <group>
      <mesh position={[0, 0.34, 0]} rotation={[Math.PI / 2, 0, 0]} castShadow>
        <capsuleGeometry args={[0.14, 0.3, 6, 12]} />
        <Mat color={body} />
      </mesh>
      {[
        [0.09, 0.14],
        [-0.09, 0.14],
        [0.09, -0.14],
        [-0.09, -0.14],
      ].map(([x, z]) => (
        <mesh key={`${x}:${z}`} position={[x ?? 0, 0.12, z ?? 0]} castShadow>
          <cylinderGeometry args={[0.035, 0.035, 0.24, 8]} />
          <Mat color={body} />
        </mesh>
      ))}
      <mesh position={[0, 0.5, -0.27]} castShadow>
        <boxGeometry args={[0.16, 0.16, 0.2]} />
        <Mat color={body} />
      </mesh>
      {[0.06, -0.06].map((x) => (
        <mesh key={x} position={[x, 0.62, -0.2]} rotation={[0, Math.PI / 2, 0]} castShadow>
          <torusGeometry args={[0.11, 0.028, 8, 16, Math.PI * 1.2]} />
          <Mat color={accent} />
        </mesh>
      ))}
    </group>
  );
}

/** A watchtower with a pointed cap. */
function Lookout({ body, accent }: ModelProps) {
  return (
    <group>
      <mesh position={[0, 0.32, 0]} castShadow>
        <cylinderGeometry args={[0.17, 0.23, 0.64, 12]} />
        <Mat color={body} />
      </mesh>
      <mesh position={[0, 0.66, 0]} castShadow>
        <cylinderGeometry args={[0.24, 0.24, 0.06, 12]} />
        <Mat color={accent} />
      </mesh>
      <mesh position={[0, 0.83, 0]} castShadow>
        <coneGeometry args={[0.24, 0.28, 12]} />
        <Mat color={body} />
      </mesh>
    </group>
  );
}

/** Three stacked, twisted blocks. */
function Mason({ body, accent }: ModelProps) {
  return (
    <group>
      <mesh position={[0, 0.1, 0]} castShadow>
        <boxGeometry args={[0.46, 0.2, 0.46]} />
        <Mat color={body} />
      </mesh>
      <mesh position={[0, 0.28, 0]} rotation={[0, 0.4, 0]} castShadow>
        <boxGeometry args={[0.34, 0.16, 0.34]} />
        <Mat color={accent} />
      </mesh>
      <mesh position={[0, 0.43, 0]} rotation={[0, 0.8, 0]} castShadow>
        <boxGeometry args={[0.22, 0.14, 0.22]} />
        <Mat color={body} />
      </mesh>
    </group>
  );
}

const MODELS: Readonly<Record<PieceType, (props: ModelProps) => JSX.Element>> = {
  B: Beacon,
  C: Climber,
  X: Ibex,
  L: Lookout,
  M: Mason,
};

/** Height of each model, used to place the pinned halo above it. */
export const MODEL_HEIGHT: Readonly<Record<PieceType, number>> = {
  B: 0.9,
  C: 0.75,
  X: 0.75,
  L: 0.98,
  M: 0.52,
};

export interface PieceModelProps {
  team: Team;
  type: PieceType;
  pinned: boolean;
}

/** One piece: its silhouette in the team body colour, a team ring at its base, and a halo when pinned. */
export function PieceModel({ team, type, pinned }: PieceModelProps) {
  const { body, ring } = TEAM_COLORS[team];
  const Model = MODELS[type];
  return (
    // Ivory faces Basalt (negative z) and Basalt faces Ivory.
    <group rotation={[0, team === 'I' ? 0 : Math.PI, 0]}>
      <mesh position={[0, 0.03, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.33, 0.045, 10, 40]} />
        <Mat color={ring} />
      </mesh>
      <Model body={body} accent={ring} />
      {pinned && (
        <mesh position={[0, MODEL_HEIGHT[type] + 0.12, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.22, 0.035, 10, 32]} />
          <Mat color={COLORS.pinned} emissive={COLORS.pinned} />
        </mesh>
      )}
    </group>
  );
}
